import { useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, Paintbrush, SquarePen, Trash2 } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import '../../../shared/styles/Orchestrator.css';

function BattleEffectList({
  effects,
  paintingId,
  onToggleVisible,
  onEditMeta,
  onTogglePaint,
  onDelete,
  onToggleGroupVisible,
  onReorder,
}) {
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  if (!effects || !effects.length) {
    return <div className='layer_list no_entity'>No battle effects yet.</div>;
  }

  const toggleCollapse = (grp) => {
    setCollapsedGroups((prev) => ({ ...prev, [grp]: !prev[grp] }));
  };

  const grouped = {};
  const ungrouped = [];
  effects.forEach((effect) => {
    const grp = (effect.group || '').trim();
    if (grp) {
      if (!grouped[grp]) grouped[grp] = [];
      grouped[grp].push(effect);
    } else {
      ungrouped.push(effect);
    }
  });

  const clearDrag = () => { setDragId(null); setDragOverId(null); };

  const handleDrop = (targetItemId, targetGroupName) => {
    if (!dragId || !onReorder) { clearDrag(); return; }
    clearDrag();
    if (dragId === targetItemId) return;
    const allItems = [...Object.values(grouped).flat(), ...ungrouped];
    const dragged = allItems.find((e) => e.id === dragId);
    if (!dragged) return;
    const rest = allItems.filter((e) => e.id !== dragId);
    const moved = { ...dragged, group: targetGroupName };
    if (targetItemId != null) {
      const idx = rest.findIndex((e) => e.id === targetItemId);
      rest.splice(idx >= 0 ? idx : rest.length, 0, moved);
    } else {
      const grpItems = rest.filter((e) => (e.group || '').trim() === targetGroupName);
      if (grpItems.length > 0) {
        const lastIdx = rest.findIndex((e) => e.id === grpItems[grpItems.length - 1].id);
        rest.splice(lastIdx + 1, 0, moved);
      } else {
        rest.push(moved);
      }
    }
    onReorder(rest);
  };

  const renderEffect = (effect) => (
    <div
      key={effect.id}
      className={`layer_row${dragId === effect.id ? ' drag_dragging' : ''}${dragOverId === effect.id ? ' drag_over_before' : ''}`}
      draggable
      onDragStart={(e) => { e.stopPropagation(); setDragId(effect.id); setDragOverId(null); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(effect.id); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(effect.id, (effect.group || '').trim()); }}
      onDragEnd={clearDrag}
    >
      <span className='drag_handle'><GripVertical size={14} /></span>
      <span className='token_name' style={{marginLeft: '8px'}} title={effect.name || effect.kind}>
        {effect.name || effect.kind}
        <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.7 }}>
          {(effect.cells || []).length} cells
        </span>
      </span>
      <div style={{ display: 'flex', gap: '4px' }}>
        <Button
          icon={effect.visible === false ? 'show' : 'hide'}
          type={effect.visible === false ? 'black-white' : 'tomato'}
          condensed
          description={effect.visible === false ? 'Show' : 'Hide'}
          onPress={() => onToggleVisible(effect.id)}
        />
        <Button
          icon={Paintbrush}
          type={paintingId === effect.id ? 'blue' : 'onyx'}
          condensed
          description={paintingId === effect.id ? 'Exit Paint Mode' : 'Paint Grid'}
          onPress={() => onTogglePaint(effect.id)}
        />
        <Button
          icon={SquarePen}
          type='black-white'
          condensed
          description='Edit Details'
          onPress={() => onEditMeta(effect)}
        />
        <Button
          icon={Trash2}
          type='cinnabar'
          condensed
          description='Delete'
          onPress={() => onDelete(effect.id)}
        />
      </div>
    </div>
  );

  return (
    <div className='layer_list'  style={{ marginTop: '-4px' }}>
      {Object.entries(grouped).map(([grp, grpEffects]) => {
        const isCollapsed = !!collapsedGroups[grp];
        const allVisible = grpEffects.every((e) => e.visible !== false);
        const isGrpOver = dragOverId === `__grp_${grp}`;
        return (
          <div key={grp} className='group_section'>
            <div
              className={`group_header${isGrpOver ? ' drag_over_group' : ''}`}
              onClick={() => toggleCollapse(grp)}
              role='button'
              tabIndex={0}
              aria-expanded={!isCollapsed}
              onKeyDown={(e) => {
                if (e.key === ' ') e.preventDefault();
                if (e.key === 'Enter' || e.key === ' ') toggleCollapse(grp);
              }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(`__grp_${grp}`); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(null, grp); }}
            >
              <button className='group_collapse_btn' tabIndex={-1} aria-hidden='true'>
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </button>
              <span className='group_name' title={grp}>{grp}</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Button
                  icon={allVisible ? 'hide' : 'show'}
                  type={allVisible ? 'tomato' : 'black-white'}
                  condensed
                  description={allVisible ? 'Hide Group' : 'Show Group'}
                  onPress={() => onToggleGroupVisible(grp)}
                />
              </div>
            </div>
            {!isCollapsed && (
              <div
                className='group_items'
                style={{paddingTop: '0px'}}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(null, grp); }}
              >
                {grpEffects.map(renderEffect)}
              </div>
            )}
          </div>
        );
      })}
      <div
        className='ungrouped_zone'
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(null, ''); }}
      >
        {ungrouped.map(renderEffect)}
      </div>
    </div>
  );
}

export default BattleEffectList;
